/**
 * NumberStepper — Number-Input mit +/- Buttons (Sprint-16-Brand).
 *
 * Editorial-Number-Input:
 *   - Zentraler Value-Display (readonly-look)
 *   - [-] und [+] Buttons an den Seiten mit Border-Trennung
 *   - Focus-Ring wandert nicht zum Input, sondern zum ganzen Container
 *   - Min/Max/Step per Prop
 *
 * Native `<input type="number">` intern behalten für Keyboard-Access + Form-Submit.
 * Nur Look ist custom — Behavior bleibt HTML-native.
 */
import { Minus, Plus } from 'lucide-react';
import { forwardRef, useCallback, type InputHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

export interface NumberStepperProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(
  ({ value, onChange, min = 0, max = 999, step = 1, disabled, className, id, ...props }, ref) => {
    const clamp = useCallback((n: number) => Math.max(min, Math.min(max, n)), [min, max]);

    const canDecrement = !disabled && value > min;
    const canIncrement = !disabled && value < max;

    return (
      <div
        className={cn(
          'border-line group flex items-stretch overflow-hidden rounded-md border bg-white',
          'focus-within:ring-sage focus-within:border-transparent focus-within:ring-2 focus-within:ring-inset',
          'duration-fast transition-colors',
          disabled && 'pointer-events-none opacity-40',
          className,
        )}
      >
        <button
          type="button"
          onClick={() => onChange(clamp(value - step))}
          disabled={!canDecrement}
          aria-label="Weniger"
          className="text-ink-soft hover:bg-card-alt hover:text-ink disabled:text-ink-faint/40 grid w-11 shrink-0 place-items-center transition-colors disabled:cursor-not-allowed"
        >
          <Minus size={14} strokeWidth={2} />
        </button>

        <input
          ref={ref}
          id={id}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (!Number.isNaN(n)) onChange(clamp(n));
          }}
          className={cn(
            'text-ink border-line/60 min-w-0 flex-1 border-x bg-transparent px-4 py-2.5 text-center text-[14px] font-medium tabular-nums outline-none',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          )}
          {...props}
        />

        <button
          type="button"
          onClick={() => onChange(clamp(value + step))}
          disabled={!canIncrement}
          aria-label="Mehr"
          className="text-ink-soft hover:bg-card-alt hover:text-ink disabled:text-ink-faint/40 grid w-11 shrink-0 place-items-center transition-colors disabled:cursor-not-allowed"
        >
          <Plus size={14} strokeWidth={2} />
        </button>
      </div>
    );
  },
);
NumberStepper.displayName = 'NumberStepper';
